// frontend/src/components/games/ScribbleGame.jsx
// FIXED CANVAS OFFSET + BEAUTIFUL RESPONSIVE UI

import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import api from '../../utils/api';

const ScribbleGame = ({ session, onLeave, onRefresh }) => {
    const [socket, setSocket] = useState(null);
    const [gameState, setGameState] = useState(null);
    const [guess, setGuess] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const [error, setError] = useState(null);
    const [roundWinner, setRoundWinner] = useState(null);
    const [showRoundResult, setShowRoundResult] = useState(false);
    const [gameWinner, setGameWinner] = useState(null);
    const [showFinalResult, setShowFinalResult] = useState(false);

    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [color, setColor] = useState('#000000');
    const [brushSize, setBrushSize] = useState(3);

    const colors = [
        '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
        '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080'
    ];

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await api.get('/auth/me');
                const userData = res.data.data || res.data.user || res.data;
                if (!userData) throw new Error('User data not found');
                setCurrentUser(userData);
            } catch (error) {
                console.error('Fetch user error:', error);
                setError('Failed to load user data. Please refresh the page.');
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (!currentUser) return;

        const socketURL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
        const newSocket = io(socketURL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
            timeout: 10000
        });

        newSocket.on('connect', () => {
            setConnectionStatus('connected');
            setError(null);

            newSocket.emit('join-game', {
                sessionCode: session.sessionCode,
                userId: currentUser._id || currentUser.id,
                userName: currentUser.name || currentUser.username
            });
        });

        newSocket.on('connect_error', (err) => {
            setConnectionStatus('error');
            setError('Connection failed. Using fallback mode.');
            loadSessionFallback();
        });

        newSocket.on('game-state', (state) => {
            setGameState(state);
            setConnectionStatus('ready');
        });

        newSocket.on('game-started', (state) => {
            setGameState(state);
            setShowRoundResult(false);
            setRoundWinner(null);
        });

        newSocket.on('player-joined', ({ userName, players }) => {
            setGameState(prev => prev ? { ...prev, players } : prev);
        });

        newSocket.on('player-drawing', (drawData) => {
            drawOnCanvas(drawData);
        });

        newSocket.on('player-guessed', ({ userId, userName, guess, isCorrect, gameState: newState }) => {
            setGameState(newState);

            if (isCorrect) {
                setRoundWinner({
                    userId,
                    userName,
                    word: newState.gameData.currentWord,
                    points: newState.players.find(p => p.userId === userId)?.score || 0
                });
                setShowRoundResult(true);

                setTimeout(() => {
                    setShowRoundResult(false);
                    setRoundWinner(null);
                }, 3000);
            }
        });

        newSocket.on('timer-update', (timeLeft) => {
            setGameState(prev => prev ? ({
                ...prev,
                gameData: { ...prev.gameData, timeLeft }
            }) : prev);
        });

        newSocket.on('time-up', (state) => {
            setGameState(state);
            setRoundWinner({ userName: 'Nobody', word: state.gameData.currentWord, points: 0 });
            setShowRoundResult(true);

            setTimeout(() => {
                setShowRoundResult(false);
                setRoundWinner(null);
            }, 3000);
        });

        newSocket.on('next-round', (state) => {
            setGameState(state);
            clearCanvas();
        });

        newSocket.on('game-finished', (data) => {
            setGameState(data.gameState);
            setGameWinner(data.winner);
            setShowFinalResult(true);
        });

        setSocket(newSocket);

        return () => {
            if (currentUser) {
                newSocket.emit('leave-game', {
                    sessionCode: session.sessionCode,
                    userId: currentUser._id || currentUser.id
                });
            }
            newSocket.disconnect();
        };
    }, [currentUser, session.sessionCode]);

    const loadSessionFallback = async () => {
        try {
            const response = await api.get(`/funzone/session/${session.sessionCode}`);
            const sessionData = response.data.data || response.data;

            const fallbackState = {
                sessionCode: sessionData.sessionCode,
                gameType: sessionData.gameType,
                status: sessionData.status,
                players: sessionData.players.map(p => ({
                    userId: p.user._id || p.user.id,
                    userName: p.user.name || p.user.username,
                    nickname: p.nickname || p.user.name || p.user.username,
                    score: p.score || 0,
                    isActive: true
                })),
                gameData: sessionData.gameData || {
                    currentWord: '',
                    currentDrawer: 0,
                    timeLeft: 60,
                    guesses: []
                },
                currentRound: sessionData.currentRound || 0,
                totalRounds: sessionData.totalRounds || 3
            };

            setGameState(fallbackState);
            setConnectionStatus('fallback');
        } catch (error) {
            console.error('Fallback load error:', error);
            setError('Failed to load game session');
        }
    };

    useEffect(() => {
        if (!socket || !gameState || gameState.status !== 'playing') return;
        if (showRoundResult) return;

        const timer = setInterval(() => {
            socket.emit('timer-tick', { sessionCode: session.sessionCode });
        }, 1000);

        return () => clearInterval(timer);
    }, [socket, gameState, session.sessionCode, showRoundResult]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }, []);

    const handleStartGame = async () => {
        if (socket && connectionStatus === 'ready') {
            socket.emit('start-game', { sessionCode: session.sessionCode });
        } else {
            try {
                await api.post(`/funzone/session/${session.sessionCode}/start`);
                await loadSessionFallback();
                alert('Game started!');
            } catch (error) {
                alert(error.response?.data?.message || 'Failed to start game');
            }
        }
    };

    const handleFinishGame = async () => {
        try {
            await api.post(`/funzone/session/${session.sessionCode}/finish`);

            const sortedPlayers = [...gameState.players].sort((a, b) => b.score - a.score);
            const winner = sortedPlayers[0];

            setGameWinner(winner);
            setShowFinalResult(true);
        } catch (error) {
            alert('Failed to finish game');
        }
    };

    // FIXED: Get accurate canvas coordinates accounting for scaling
    const getCanvasCoordinates = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        return { x, y };
    };

    const startDrawing = (e) => {
        if (!isMyTurn() || showRoundResult) return;
        setIsDrawing(true);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { x, y } = getCanvasCoordinates(e);

        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
    };

    const draw = (e) => {
        if (!isDrawing || !isMyTurn() || showRoundResult) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const { x, y } = getCanvasCoordinates(e);

        ctx.lineWidth = brushSize;
        ctx.strokeStyle = color;
        ctx.lineTo(x, y);
        ctx.stroke();

        if (socket && connectionStatus === 'ready') {
            socket.emit('game-action', {
                sessionCode: session.sessionCode,
                action: 'draw',
                data: { x, y, color, brushSize }
            });
        }
    };

    const drawOnCanvas = ({ x, y, color, brushSize }) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.lineWidth = brushSize;
        ctx.strokeStyle = color;
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const handleGuess = async (e) => {
        e.preventDefault();
        if (!guess.trim() || isMyTurn() || showRoundResult) return;

        if (socket && connectionStatus === 'ready') {
            socket.emit('game-action', {
                sessionCode: session.sessionCode,
                action: 'guess',
                data: { guess }
            });
        }

        setGuess('');
    };

    const isMyTurn = () => {
        if (!gameState || !currentUser) return false;
        const drawerIndex = gameState.gameData?.currentDrawer || 0;
        const currentUserId = currentUser._id || currentUser.id;
        return gameState.players[drawerIndex]?.userId === currentUserId;
    };

    const getCurrentDrawer = () => {
        if (!gameState) return null;
        const drawerIndex = gameState.gameData?.currentDrawer || 0;
        return gameState.players[drawerIndex];
    };

    const amIWinner = () => {
        if (!roundWinner || !currentUser) return false;
        const currentUserId = currentUser._id || currentUser.id;
        return roundWinner.userId === currentUserId;
    };

    const amIGameWinner = () => {
        if (!gameWinner || !currentUser) return false;
        const currentUserId = currentUser._id || currentUser.id;
        return gameWinner.userId === currentUserId;
    };

    if (!currentUser || !gameState) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white flex items-center justify-center">
                <div className="text-center max-w-md">
                    {error ? (
                        <>
                            <div className="text-6xl mb-4">⚠️</div>
                            <p className="text-2xl font-bold mb-2">Connection Issue</p>
                            <p className="text-gray-300 mb-6">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition"
                            >
                                Refresh Page
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
                            <p className="text-xl">Loading game...</p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Round Result Modal
    if (showRoundResult && roundWinner) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white flex items-center justify-center p-4">
                <div className="text-center max-w-2xl animate-bounce">
                    {amIWinner() ? (
                        <>
                            <div className="text-9xl mb-6">🎉</div>
                            <h1 className="text-7xl font-bold mb-4 text-green-400 drop-shadow-lg">YOU WIN!</h1>
                            <p className="text-4xl mb-6">The word was: <span className="text-yellow-400 font-bold">{roundWinner.word}</span></p>
                            <p className="text-2xl text-gray-200">+{roundWinner.points} points!</p>
                        </>
                    ) : roundWinner.userName === 'Nobody' ? (
                        <>
                            <div className="text-9xl mb-6">⏰</div>
                            <h1 className="text-7xl font-bold mb-4 text-orange-400 drop-shadow-lg">TIME'S UP!</h1>
                            <p className="text-4xl mb-6">The word was: <span className="text-yellow-400 font-bold">{roundWinner.word}</span></p>
                            <p className="text-2xl text-gray-200">Nobody guessed it!</p>
                        </>
                    ) : (
                        <>
                            <div className="text-9xl mb-6">😢</div>
                            <h1 className="text-7xl font-bold mb-4 text-red-400 drop-shadow-lg">YOU LOSE!</h1>
                            <p className="text-4xl mb-6"><span className="text-green-400 font-bold">{roundWinner.userName}</span> won this round!</p>
                            <p className="text-2xl text-gray-200">The word was: <span className="text-yellow-400 font-bold">{roundWinner.word}</span></p>
                        </>
                    )}
                    <p className="text-sm text-gray-400 mt-8 animate-pulse">Next round starting...</p>
                </div>
            </div>
        );
    }

    // Final Game Result Modal
    if (showFinalResult && gameWinner) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-pink-900 text-white flex items-center justify-center p-4">
                <div className="text-center max-w-4xl">
                    <div className="text-9xl mb-6">🏆</div>
                    <h1 className="text-8xl font-bold mb-6 drop-shadow-2xl">GAME OVER!</h1>

                    {amIGameWinner() ? (
                        <>
                            <div className="text-9xl mb-6 animate-bounce">🎊</div>
                            <h2 className="text-6xl font-bold mb-4 text-yellow-400 drop-shadow-lg">YOU ARE THE CHAMPION!</h2>
                            <p className="text-4xl mb-8">Final Score: <span className="text-green-400 font-bold">{gameWinner.score} points</span></p>
                        </>
                    ) : (
                        <>
                            <h2 className="text-5xl font-bold mb-4"><span className="text-yellow-400">{gameWinner.userName}</span> Wins!</h2>
                            <p className="text-3xl mb-8">Final Score: <span className="text-green-400 font-bold">{gameWinner.score} points</span></p>
                        </>
                    )}

                    <div className="bg-gray-800 bg-opacity-70 backdrop-blur-sm rounded-3xl p-8 mb-8 shadow-2xl">
                        <h3 className="text-4xl font-bold mb-6">Final Scores</h3>
                        <div className="space-y-4">
                            {[...gameState.players]
                                .sort((a, b) => b.score - a.score)
                                .map((player, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-gray-700 bg-opacity-70 rounded-xl p-6 hover:bg-opacity-90 transition">
                                        <div className="flex items-center gap-4">
                                            <span className="text-5xl">
                                                {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                                            </span>
                                            <span className="text-3xl font-bold">{player.userName}</span>
                                        </div>
                                        <span className="text-4xl font-bold text-yellow-400">{player.score} pts</span>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <button
                        onClick={onLeave}
                        className="px-12 py-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl font-bold text-2xl hover:from-purple-700 hover:to-pink-700 shadow-2xl transform hover:scale-105 transition"
                    >
                        Back to Lobby
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-2 sm:p-4 md:p-6">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="flex flex-col sm:flex-row justify-between items-center bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-4 gap-4 shadow-xl">
                    <div className="text-center sm:text-left">
                        <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-2 justify-center sm:justify-start">
                            🎨 Scribble
                            {connectionStatus === 'ready' && (
                                <span className="px-2 py-1 bg-green-500 text-xs rounded-full animate-pulse">● Live</span>
                            )}
                            {connectionStatus === 'fallback' && (
                                <span className="px-2 py-1 bg-yellow-500 text-xs rounded-full">Offline</span>
                            )}
                        </h1>
                        <p className="text-sm text-gray-300">Session: #{session.sessionCode}</p>
                        {gameState.status === 'playing' && (
                            <p className="text-sm text-yellow-400 font-semibold">
                                Round {gameState.currentRound}/{gameState.totalRounds}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {gameState.status === 'playing' && (
                            <div className="text-center bg-gradient-to-br from-yellow-500 to-orange-500 px-6 py-3 rounded-xl shadow-lg">
                                <div className="text-4xl font-bold">{gameState.gameData?.timeLeft || 0}s</div>
                                <div className="text-xs">Time Left</div>
                            </div>
                        )}
                        {gameState.status === 'playing' && (
                            <button
                                onClick={handleFinishGame}
                                className="px-4 py-2 bg-purple-600 rounded-xl hover:bg-purple-700 transition shadow-lg"
                            >
                                End Game
                            </button>
                        )}
                        <button
                            onClick={onLeave}
                            className="px-4 py-2 bg-red-600 rounded-xl hover:bg-red-700 transition shadow-lg"
                        >
                            Leave
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Players Panel */}
                <div className="bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <span>👥</span> Players ({gameState.players.filter(p => p.isActive).length})
                    </h3>
                    <div className="space-y-3">
                        {gameState.players.map((player, idx) => (
                            <div
                                key={idx}
                                className={`rounded-xl p-4 transition transform hover:scale-105 ${!player.isActive ? 'opacity-50' : ''
                                    } ${gameState.status === 'playing' &&
                                        idx === gameState.gameData?.currentDrawer
                                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 ring-4 ring-yellow-300 shadow-xl'
                                        : 'bg-gray-700 bg-opacity-70'
                                    }`}
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <span className="font-bold text-lg">{player.userName}</span>
                                        {gameState.status === 'playing' &&
                                            idx === gameState.gameData?.currentDrawer && (
                                                <div className="text-xs mt-1 font-semibold">🎨 Drawing Now</div>
                                            )}
                                        {!player.isActive && (
                                            <div className="text-xs text-gray-400">(Offline)</div>
                                        )}
                                    </div>
                                    <span className="text-yellow-400 font-bold text-2xl">{player.score}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {gameState.status === 'waiting' && (
                        <button
                            onClick={handleStartGame}
                            className="w-full mt-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition shadow-xl transform hover:scale-105"
                        >
                            🎮 Start Game
                        </button>
                    )}
                </div>

                {/* Canvas Area */}
                <div className="lg:col-span-2 bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
                    {gameState.status === 'playing' && (
                        <>
                            <div className="mb-4 text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-4 shadow-lg">
                                {isMyTurn() ? (
                                    <p className="text-lg">
                                        <span className="text-sm">Draw this word:</span>
                                        <br />
                                        <span className="text-4xl font-bold text-yellow-300">{gameState.gameData?.currentWord}</span>
                                    </p>
                                ) : (
                                    <p className="text-xl font-semibold">
                                        🎨 <span className="text-yellow-300">{getCurrentDrawer()?.userName}</span> is drawing!
                                        <br />
                                        <span className="text-sm">Guess what it is!</span>
                                    </p>
                                )}
                            </div>

                            {/* Drawing Tools */}
                            {isMyTurn() && (
                                <div className="mb-4 bg-gray-700 bg-opacity-70 rounded-xl p-4">
                                    <div className="flex flex-wrap gap-3 items-center justify-center">
                                        <div className="flex gap-2">
                                            {colors.map(c => (
                                                <button
                                                    key={c}
                                                    onClick={() => setColor(c)}
                                                    className={`w-10 h-10 rounded-full border-4 transition transform hover:scale-110 ${color === c ? 'border-white scale-110' : 'border-gray-500'
                                                        }`}
                                                    style={{ backgroundColor: c }}
                                                />
                                            ))}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setBrushSize(3)}
                                                className={`px-4 py-2 rounded-lg font-semibold transition ${brushSize === 3 ? 'bg-blue-500' : 'bg-gray-600 hover:bg-gray-500'
                                                    }`}
                                            >
                                                S
                                            </button>
                                            <button
                                                onClick={() => setBrushSize(6)}
                                                className={`px-4 py-2 rounded-lg font-semibold transition ${brushSize === 6 ? 'bg-blue-500' : 'bg-gray-600 hover:bg-gray-500'
                                                    }`}
                                            >
                                                M
                                            </button>
                                            <button
                                                onClick={() => setBrushSize(10)}
                                                className={`px-4 py-2 rounded-lg font-semibold transition ${brushSize === 10 ? 'bg-blue-500' : 'bg-gray-600 hover:bg-gray-500'
                                                    }`}
                                            >
                                                L
                                            </button>
                                        </div>
                                        <button
                                            onClick={clearCanvas}
                                            className="px-6 py-2 bg-red-600 rounded-lg hover:bg-red-700 font-semibold transition"
                                        >
                                            🗑️ Clear
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Canvas - FIXED SIZE */}
                    <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                        <canvas
                            ref={canvasRef}
                            width={800}
                            height={600}
                            className={`w-full h-auto ${isMyTurn() && !showRoundResult ? 'cursor-crosshair' : 'cursor-not-allowed'
                                }`}
                            onMouseDown={startDrawing}
                            onMouseUp={stopDrawing}
                            onMouseMove={draw}
                            onMouseLeave={stopDrawing}
                        />
                    </div>

                    {gameState.status === 'waiting' && (
                        <div className="text-center py-16">
                            <div className="text-6xl mb-4">🎮</div>
                            <p className="text-2xl font-bold text-gray-300 mb-2">Waiting for game to start...</p>
                            <p className="text-sm text-gray-400">Need at least 2 players to begin</p>
                        </div>
                    )}
                </div>

                {/* Chat/Guess Panel */}
                <div className="bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-4 flex flex-col shadow-xl">
                    <h3 className="text-xl font-bold mb-4">
                        {isMyTurn() ? '💭 Guesses' : '✏️ Guess the Word!'}
                    </h3>

                    {/* Chat Messages */}
                    <div className="flex-1 bg-gray-900 bg-opacity-50 rounded-xl p-4 mb-4 overflow-y-auto" style={{ height: '400px' }}>
                        {(!gameState.gameData?.guesses || gameState.gameData.guesses.length === 0) ? (
                            <p className="text-center text-gray-400 py-12">No guesses yet...</p>
                        ) : (
                            <div className="space-y-2">
                                {gameState.gameData.guesses.map((msg, idx) => (
                                    <div
                                        key={idx}
                                        className={`p-3 rounded-lg transition transform hover:scale-105 ${msg.isCorrect
                                                ? 'bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg'
                                                : 'bg-gray-700 bg-opacity-70'
                                            }`}
                                    >
                                        <span className="font-bold">{msg.userName}: </span>
                                        <span>{msg.text}</span>
                                        {msg.isCorrect && <span className="ml-2 text-yellow-300">🎉 WINNER!</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Guess Input */}
                    {!isMyTurn() && gameState.status === 'playing' && !showRoundResult && (
                        <form onSubmit={handleGuess} className="flex gap-2">
                            <input
                                type="text"
                                value={guess}
                                onChange={(e) => setGuess(e.target.value)}
                                placeholder="Type your guess..."
                                className="flex-1 px-4 py-3 bg-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-500 transition"
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl hover:from-blue-700 hover:to-purple-700 font-bold transition shadow-lg transform hover:scale-105"
                            >
                                Send
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScribbleGame;